import { ColumnDef } from '@tanstack/react-table'
import { ChevronDown, ChevronRight } from 'lucide-react'

import type {
	ProviderBillingBreakdown,
	PromotionBillingBreakdown,
} from '../../type'

const formatCurrency = (value: number): string => {
	return `฿${value.toLocaleString('th-TH', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`
}

const MONTH_NAMES = [
	'มกราคม',
	'กุมภาพันธ์',
	'มีนาคม',
	'เมษายน',
	'พฤษภาคม',
	'มิถุนายน',
	'กรกฎาคม',
	'สิงหาคม',
	'กันยายน',
	'ตุลาคม',
	'พฤศจิกายน',
	'ธันวาคม',
]

export const formatBillingMonth = (monthStr: string): string => {
	const [year, month] = monthStr.split('-')
	const monthIndex = parseInt(month, 10) - 1
	const buddhistYear = parseInt(year, 10) + 543
	return `${MONTH_NAMES[monthIndex]} ${buddhistYear}`
}

export const providerColumns: ColumnDef<ProviderBillingBreakdown>[] = [
	{
		id: 'expander',
		header: () => <div className="w-8"></div>,
		cell: ({ row }) => {
			const hasPromotions =
				row.original.promotions && row.original.promotions.length > 0
			if (!hasPromotions) return null

			return (
				<button
					onClick={() => row.toggleExpanded()}
					className="flex h-8 w-8 items-center justify-center rounded hover:bg-muted"
				>
					{row.getIsExpanded() ? (
						<ChevronDown className="h-4 w-4" />
					) : (
						<ChevronRight className="h-4 w-4" />
					)}
				</button>
			)
		},
	},
	{
		accessorKey: 'providerName',
		header: 'ผู้ให้บริการ',
		cell: ({ row }) => {
			const promotionCount = row.original.promotions?.length || 0
			return (
				<div className="flex items-center gap-2">
					<span className="font-medium">{row.original.providerName}</span>
					{promotionCount > 0 && (
						<span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
							ดูรายละเอียดโปรโมชั่นเพิ่มเติม
						</span>
					)}
				</div>
			)
		},
	},
	{
		accessorKey: 'companyRate',
		header: () => <div className="text-right">อัตราคอมพานี</div>,
		cell: ({ row }) => (
			<div className="text-right">
				{(row.original.companyRate * 100).toFixed(0)}%
			</div>
		),
	},
	{
		accessorKey: 'transferIn',
		header: () => <div className="text-right">ยอดโอนเข้า</div>,
		cell: ({ row }) => (
			<div className="text-right text-green-600">
				{formatCurrency(row.original.transferIn)}
			</div>
		),
	},
	{
		accessorKey: 'transferOut',
		header: () => <div className="text-right">ยอดโอนออก</div>,
		cell: ({ row }) => (
			<div className="text-right text-red-600">
				{formatCurrency(row.original.transferOut)}
			</div>
		),
	},
	{
		accessorKey: 'profit',
		header: () => <div className="text-right">กินลูกค้า</div>,
		cell: ({ row }) => (
			<div className="text-right text-emerald-600">
				{formatCurrency(row.original.profit)}
			</div>
		),
	},
	{
		accessorKey: 'nonPromotionCharge',
		header: () => (
			<div className="text-right">ยอดเรียกเก็บ (ไม่รวมโปรโมชั่น)</div>
		),
		cell: ({ row }) => (
			<div className="text-right font-semibold text-purple-600">
				{formatCurrency(row.original.nonPromotionCharge)}
			</div>
		),
	},
	{
		accessorKey: 'promotionCharge',
		header: () => <div className="text-right">ยอดเรียกเก็บ (โปรโมชั่น)</div>,
		cell: ({ row }) => (
			<div className="text-right font-semibold text-orange-600">
				{formatCurrency(row.original.promotionCharge)}
			</div>
		),
	},
	{
		accessorKey: 'charge',
		header: () => <div className="text-right">ยอดเรียกเก็บรวม</div>,
		cell: ({ row }) => (
			<div className="text-right font-semibold text-blue-600">
				{formatCurrency(row.original.charge)}
			</div>
		),
	},
]

export const promotionColumns: ColumnDef<PromotionBillingBreakdown>[] = [
	{
		accessorKey: 'promotionName',
		header: 'ชื่อโปรโมชั่น',
		cell: ({ row }) => (
			<span className="font-medium">
				{row.original.promotionName || 'ไม่มีโปรโมชั่น'}
			</span>
		),
	},
	{
		accessorKey: 'chargeRate',
		header: () => <div className="text-right">อัตรา</div>,
		cell: ({ row }) => {
			const chargeRate = row.original.chargeRate
			if (chargeRate === null) {
				return <div className="text-right text-muted-foreground">-</div>
			}
			return <div className="text-right">{(chargeRate * 100).toFixed(0)}%</div>
		},
	},
	{
		accessorKey: 'transferIn',
		header: () => <div className="text-right">ยอดโอนเข้า</div>,
		cell: ({ row }) => (
			<div className="text-right text-green-600">
				{formatCurrency(row.original.transferIn)}
			</div>
		),
	},
	{
		accessorKey: 'transferOut',
		header: () => <div className="text-right">ยอดโอนออก</div>,
		cell: ({ row }) => (
			<div className="text-right text-red-600">
				{formatCurrency(row.original.transferOut)}
			</div>
		),
	},
	{
		accessorKey: 'profit',
		header: () => <div className="text-right">กินลูกค้า</div>,
		cell: ({ row }) => (
			<div className="text-right text-emerald-600">
				{formatCurrency(row.original.profit)}
			</div>
		),
	},
	{
		accessorKey: 'charge',
		header: () => <div className="text-right">ยอดเรียกเก็บ</div>,
		cell: ({ row }) => {
			const charge = row.original.charge
			if (charge === null) {
				return <div className="text-right text-muted-foreground">-</div>
			}
			return (
				<div className="text-right font-semibold text-blue-600">
					{formatCurrency(charge)}
				</div>
			)
		},
	},
]
